import FullScreenLoader from '@/components/full-screen-loader'
import {
  getEnterpriseAdminGetEnterpriseAdminGetOptions,
  getOrganizationInfoOrganizationInfoGetOptions,
} from '@/generated/@tanstack/react-query.gen'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { createContext, ReactNode, useContext, useEffect } from 'react'
import {
  paymentMethodDTO,
  PaymentMethodType,
} from './_data/payment-method-schema'
import {
  EnterpriseInformationType,
  paymentEnterpriseInformationDTO,
} from './_data/enterprise-info-schema'
import {
  BillingContact,
  billingContactSchema,
} from './_data/payment-billing-contact-schema'
import {
  paymentCurrentPlanDTO,
  PaymentCurrentPlanType,
} from './_data/payment-current-plant-schema'

interface UserContextType {
  organizationId: number
  availableSeats: number
  fullname: string
  id: string
  role: number
  paymentMethods: PaymentMethodType[]
  paymentEnterpriseInformation: EnterpriseInformationType
  billingContacts: BillingContact[]
  paymentCurrentPlan: PaymentCurrentPlanType
  onUpdateSeats: () => void
}

export const UserContext = createContext<UserContextType | undefined>(undefined)

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

export function UserProvider({ children }: { children: ReactNode }) {
  const isHasAuth = !isEmpty(localStorage.getItem('auth'))

  const { data, error, isLoading } = useQuery({
    ...getEnterpriseAdminGetEnterpriseAdminGetOptions({}),
    retry: false,
    staleTime: 0, // Data is always stale
    refetchOnMount: 'always', // Always refetch when the component mounts
    refetchOnWindowFocus: false, // Refetch when the browser window regains focus
    gcTime: 0, // Prevent long-term caching in memory
    select: (data) => {
      return {
        admin: data.admin,
        status: data.status,
        role: data.useraccesslevel,
        fullname: data.fullname,
        id: data.useruuid,
      }
    },
    enabled: isHasAuth,
  })

  const {
    data: organization,
    isLoading: isOrganizationLoading,
    refetch,
  } = useQuery({
    ...getOrganizationInfoOrganizationInfoGetOptions({}),
    enabled: !!data,
    select: (data) => {
      const paymentMethod = paymentMethodDTO(data.payment_methods)
      const paymentEnterpriseInformation = paymentEnterpriseInformationDTO(data)
      const billingContacts =
        billingContactSchema.array().safeParse(data.billing_contacts).data || []
      const paymentCurrentPlan = paymentCurrentPlanDTO(data.current_plan)

      return {
        availableSeats: data.available_seats,
        organizationId: data.id,
        paymentMethods: paymentMethod,
        paymentEnterpriseInformation: paymentEnterpriseInformation,
        billingContacts: billingContacts,
        paymentCurrentPlan: paymentCurrentPlan,
      }
    },
  })

  useEffect(() => {
    if (error) {
      window.location.href = '/sign-in'
    }
  }, [error])

  if (!isHasAuth) {
    window.location.replace('/sign-in')
    return <FullScreenLoader />
  }

  if (isLoading || isOrganizationLoading) {
    return <FullScreenLoader title="Checking Authenticate..." />
  }

  if (
    data?.admin &&
    data.status === 'success' &&
    organization?.organizationId
  ) {
    return (
      <UserContext.Provider
        value={{
          ...organization,
          ...data,
          onUpdateSeats: () => {
            refetch()
          },
        }}
      >
        {children}
      </UserContext.Provider>
    )
  }

  localStorage.removeItem('auth')
  window.location.replace('/')

  return <FullScreenLoader />
}
